import { type ReactElement } from 'react';
import { Folder, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isSystemGalleryFolderKind, type GalleryFolder } from '@tailoring-crm/shared';
import { galleryFolderLabel } from '../../lib/gallery/gallery-folder-label.js';
import { cn, Button } from '@tailoring-crm/ui';

/**
 * The folder tree, flattened for rendering.
 *
 * A root folder is depth 1. Siblings are ordered system folders first (by `sortOrder`), then
 * custom ones — never by comparing a `name`, because system-ness is `isSystemGalleryFolderKind`
 * alone.
 */
export interface FolderNode {
  folder: GalleryFolder;
  depth: number;
  children: FolderNode[];
}

export function buildFolderTree(folders: readonly GalleryFolder[]): FolderNode[] {
  const byParent = new Map<string | null, GalleryFolder[]>();
  for (const folder of folders) {
    const list = byParent.get(folder.parentId) ?? [];
    list.push(folder);
    byParent.set(folder.parentId, list);
  }

  const sortSiblings = (list: GalleryFolder[]): GalleryFolder[] =>
    [...list].sort((a, b) => {
      const aSystem = isSystemGalleryFolderKind(a.kind);
      const bSystem = isSystemGalleryFolderKind(b.kind);
      if (aSystem !== bSystem) return aSystem ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    });

  const build = (parentId: string | null, depth: number): FolderNode[] =>
    sortSiblings(byParent.get(parentId) ?? []).map((folder) => ({
      folder,
      depth,
      children: build(folder.id, depth + 1),
    }));

  return build(null, 1);
}

export function flattenFolderTree(nodes: readonly FolderNode[]): FolderNode[] {
  return nodes.flatMap((node) => [node, ...flattenFolderTree(node.children)]);
}

export interface GalleryFolderTreeProps {
  folders: readonly GalleryFolder[];
  /** A folder id, `'unfiled'`, or null when nothing is picked. */
  selectedId: string | null;
  onSelectFolder: (folderId: string) => void;
  onSelectUnfiled: () => void;
  onRename: (folder: GalleryFolder) => void;
  onDelete: (folder: GalleryFolder) => void;
}

export function GalleryFolderTree({
  folders,
  selectedId,
  onSelectFolder,
  onSelectUnfiled,
  onRename,
  onDelete,
}: GalleryFolderTreeProps): ReactElement {
  const { t } = useTranslation();
  const rows = flattenFolderTree(buildFolderTree(folders));

  return (
    <nav aria-label={t('gallery.foldersLabel')} className="flex flex-col gap-1">
      <ul className="flex flex-col gap-1">
        {rows.map(({ folder, depth }) => {
          // System-ness is decided ONLY by the kind — never by comparing the folder's name.
          const system = isSystemGalleryFolderKind(folder.kind);
          const label = galleryFolderLabel(t, folder);
          const active = selectedId === folder.id;

          return (
            <li key={folder.id} data-testid="gallery-folder-row">
              <div
                style={{ paddingLeft: `${(depth - 1) * 16}px` }}
                className={cn(
                  'flex items-center gap-1 rounded-md pr-1 text-sm',
                  active ? 'bg-accent font-medium' : 'hover:bg-accent/60',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelectFolder(folder.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 truncate px-2 py-2 text-left"
                >
                  <Folder aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{label}</span>
                  <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                    {folder.mediaCount}
                  </span>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  onClick={() => onRename(folder)}
                  aria-label={t('gallery.folder.renameAction', { name: label })}
                >
                  <Pencil aria-hidden="true" className="size-3.5" />
                </Button>
                {/* Delete is offered on a custom folder ONLY — absent from the DOM for a system
                    one, not present-and-disabled. */}
                {!system ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 text-destructive"
                    onClick={() => onDelete(folder)}
                    aria-label={t('gallery.folder.deleteAction', { name: label })}
                  >
                    <Trash2 aria-hidden="true" className="size-3.5" />
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={onSelectUnfiled}
        data-testid="gallery-unfiled-row"
        className={cn(
          'flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm',
          selectedId === 'unfiled' ? 'bg-accent font-medium' : 'hover:bg-accent/60',
        )}
      >
        {t('gallery.unfiled')}
      </button>
    </nav>
  );
}
